import { useState, useCallback } from "react";
import { FiAlertCircle } from "react-icons/fi";
import { useToast } from "@chakra-ui/react";
import { cn } from "utils/helpers";
import { useToggleScoring } from "../../../../hooks/usePanelScoringQueries";

type LeadScoreVisibility = "always" | "only_when_not_scoring" | "only_after_all_submitted";

interface LeadScoringToggleProps {
	anyMemberSubmitted?: boolean;
	evalId: number;
	initialIsScoring: boolean;
	leadScoreVisibility?: LeadScoreVisibility;
	rfxId: number;
	onToggle?: (isScoring: boolean) => void;
}

export const LeadScoringToggle: React.FC<LeadScoringToggleProps> = ({
	rfxId,
	evalId,
	initialIsScoring,
	leadScoreVisibility = "always",
	anyMemberSubmitted = false,
	onToggle,
}) => {
	const [isScoring, setIsScoring] = useState(initialIsScoring);
	const toggleMutation = useToggleScoring(rfxId, evalId);
	const toast = useToast();

	const handleToggle = useCallback(() => {
		const newValue = !isScoring;

		// Prevent enabling scoring after members have submitted when visibility is restricted
		if (newValue && leadScoreVisibility !== "always" && anyMemberSubmitted) {
			toast({
				title: "Cannot enable scoring",
				description: "Cannot enable scoring after panel members have submitted scores.",
				status: "warning",
				duration: 5000,
				isClosable: true,
			});
			return;
		}

		setIsScoring(newValue);
		onToggle?.(newValue);
		toggleMutation.mutate(
			{ is_scoring: newValue },
			{
				onError: () => {
					setIsScoring(!newValue);
					onToggle?.(!newValue);
				},
			}
		);
	}, [isScoring, toggleMutation, leadScoreVisibility, anyMemberSubmitted, toast, onToggle]);

	return (
		<div
			className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-100 px-4 py-3"
			data-testid="lead-scoring-toggle"
		>
			<div className="flex items-center gap-3">
				<div className="flex items-center gap-3">
					{toggleMutation.isError && (
						<span className="inline-flex items-center gap-1 text-xs text-red-600">
							<FiAlertCircle size={12} />
							Failed to update
						</span>
					)}
					<button
						type="button"
						role="switch"
						aria-checked={isScoring}
						aria-label="Toggle lead scoring participation"
						onClick={handleToggle}
						disabled={toggleMutation.isPending}
						className={cn(
							"relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0031DD] focus-visible:ring-offset-2",
							isScoring ? "bg-[#0031DD]" : "bg-gray-300",
							toggleMutation.isPending && "opacity-50 cursor-not-allowed"
						)}
						data-testid="scoring-toggle-switch"
					>
						<span
							className={cn(
								"pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm ring-0 transition-transform",
								isScoring ? "translate-x-6" : "translate-x-1"
							)}
						/>
					</button>
				</div>
				<div>
					<p className="text-sm font-medium text-gray-900">
						I am scoring this evaluation
					</p>
					<p className="text-xs text-gray-500">
						{isScoring
							? "You will score suppliers alongside other panel members."
							: "You will not score suppliers — only manage the panel."}
					</p>
				</div>
			</div>
		</div>
	);
};
